/**
 * Navigation registry (requirements 9.1 / 9.2): the modules of OneSphere along the business lines of the group —
 * Human capital, Product management, Order management, Procurement, Inventory, Plant operations, Fleet (the OneFleet
 * add-on, D-15/D-16), Receivables, Payables, Projects, Service, CRM, Incentive compensation — plus My dashboard, the
 * Executive cockpit, ERP setup and Administration.
 * Each module lists its entities: an experience screen (a page or a tab of a page) or an ERP record type opened through
 * One source for the landing hub, the breadcrumbs and the module menu.
 *
 * Roles are the os_* realm roles of the token; a module is shown when the person holds one of its roles, an entity when
 * it holds one of the entity's roles (the module's when the entity names none). Frappe permissions still decide what a
 * list screen returns.
 *
 * Breadcrumbs: Home › Module › Entity › Record. The location is matched against every entity of every module — a tab
 * entity by exact page and tab, a record prefix (`paths`) or a plain page by prefix — and the best match wins; ties keep
 * the first module in registry order, so a page shared by two modules (the order pipeline in Order management and CRM)
 * belongs to the first one. What follows the matched prefix is the record.
 */
import { SCREENS, screenLabel } from "../erp/registry";
import { FLEET_MODULE_ROLES, FLEET_SECTIONS, fleetPath } from "./onefleet/routes";
import { WAREHOUSE_SECTIONS, warehousePath } from "./warehouse/routes";

export type NavEntity = {
  key: string;
  /** Route; a tab of a page is `/page?tab=x`. */
  to: string;
  /** i18n key of the label; ERP record types take the registry label instead. */
  label?: string;
  /** ERP record type opened through the generated list screen. */
  doctype?: string;
  roles?: readonly string[];
  /** Extra route prefixes that belong to this entity (record pages). */
  paths?: readonly string[];
};
export type NavModule = {
  key: string;
  to: string;
  label: string;
  desc: string;
  color: string;
  roles: readonly string[];
  entities: NavEntity[];
  /** Cross-cutting module (My dashboard, ERP setup, Administration) shown apart from the business lines. */
  system?: boolean;
};

const ANY: readonly string[] = [];
const ADMIN = ["os_admin"];
const EXEC = ["os_executive", ...ADMIN];
const FINANCE = ["os_finance", ...EXEC];
const PLANT = ["os_plant_gm", "os_maintenance", "os_operator", ...EXEC];
const COMMERCIAL = ["os_sales", "os_store_manager", "os_cashier", ...FINANCE, "os_plant_gm"];
const BUYERS = ["os_procurement", "os_project_manager", "os_plant_gm", "os_maintenance", ...FINANCE];
const STOCK = ["os_procurement", "os_plant_gm", "os_maintenance", "os_store_manager", "os_cashier", "os_sales", ...FINANCE];
const HR = ["os_hr", "os_plant_gm", ...EXEC];
// an ERPNext screen is a super administrator's (owner's rule, 2026-09-21): whatever roles a module names, the entity keeps ADMIN

export const NAV: NavModule[] = [
  { key: "my", to: "/my", label: "nav.my.title", desc: "nav.my.desc", color: "bg-brand-primary", roles: ANY, system: true, entities: [] },
  { key: "executive", to: "/executive", label: "nav.executive.title", desc: "nav.executive.desc", color: "bg-brand-primary", roles: ["os_plant_gm", "os_project_manager", "os_hr", ...FINANCE],
    entities: [
      { key: "board", to: "/executive", label: "nav.executive.board", roles: ["os_plant_gm", ...FINANCE] },
      { key: "finance", to: "/finance", label: "nav.executive.finance", roles: FINANCE },
      { key: "reports", to: "/reports", label: "nav.executive.reports", roles: ANY },
      { key: "process", to: "/process", label: "nav.executive.process", roles: ["os_plant_gm", "os_project_manager", "os_hr", ...FINANCE] },
      { key: "demoMetrics", to: "/demo-metrics", label: "nav.executive.demoMetrics", roles: FINANCE },
    ] },
  { key: "humanCapital", to: "/people", label: "nav.humanCapital.title", desc: "nav.humanCapital.desc", color: "bg-brand-deep", roles: HR,
    entities: [
      { key: "directory", to: "/people/directory", label: "nav.humanCapital.directory" },
      { key: "lifecycle", to: "/people/lifecycle", label: "nav.humanCapital.lifecycle" },
      { key: "recruitment", to: "/people/recruitment", label: "nav.humanCapital.recruitment" },
      { key: "workforce", to: "/people/workforce", label: "nav.humanCapital.workforce" },
      { key: "compliance", to: "/people/compliance", label: "nav.humanCapital.compliance" },
      { key: "training", to: "/people/learning", label: "nav.humanCapital.training" },
      { key: "assignments", to: "/people/assignments", label: "nav.humanCapital.assignments" },
      // Leave, Expenses, Payroll and Appraisals are top-level People screens too (they were split out of the
      // lifecycle page); without an entity of their own the module menu could not reach them and the help
      // marker had nothing to point at from outside the module. Payroll carries the module's payroll roles.
      { key: "leave", to: "/people/leave", label: "ppl.tabs.leave" },
      { key: "expenses", to: "/people/expenses", label: "ppl.tabs.expenses" },
      { key: "payroll", to: "/people/payroll", label: "ppl.tabs.payroll", roles: ["os_hr", ...FINANCE] },
      { key: "appraisals", to: "/people/appraisals", label: "ppl.tabs.appraisals" },
      // The employee editor: registered so the trail names it rather than printing the raw "new" segment. The
      // edit route of an existing person is not registered — its trail should read Home › Human capital ›
      // Employees › HR-EMP-00010, which the directory entity already produces.
      { key: "employee-new", to: "/people/directory/new", label: "emp.form.addTitle" },
      // second-level screens: they reuse the labels their sub-navigation already shows, so the breadcrumb names
      // them instead of printing the raw path segment. They sit after the top-level screens, which is what
      // the module menu lists first.
      { key: "expenses-advances", to: "/people/expenses/advances", label: "exp.tabs.advances" },
      { key: "expenses-travel", to: "/people/expenses/travel", label: "exp.tabs.travel" },
      { key: "learning-outcomes", to: "/people/learning/outcomes", label: "ld.tabs.outcomes" },
      { key: "people-conduct", to: "/people/conduct", label: "ppl.tabs.conduct" },
      { key: "people-files", to: "/people/files", label: "ppl.tabs.files" },
      { key: "appraisals-scoring", to: "/people/appraisals/scoring", label: "apr.tabs.scoring" },
      { key: "payroll-gratuity", to: "/people/payroll/gratuity", label: "pay.tabs.gratuity" },
      { key: "payroll-adjustments", to: "/people/payroll/adjustments", label: "pay.tabs.adjustments" },
      { key: "leave-accrual", to: "/people/leave/accrual", label: "lv.tabs.accrual" },
      { key: "leave-compoff", to: "/people/leave/compoff", label: "lv.tabs.compoff" },
      { key: "lifecycle-approvals", to: "/people/lifecycle/approvals", label: "hr.tabs.approvals" },
      { key: "lifecycle-gate", to: "/people/lifecycle/gate", label: "hr.tabs.gate" },
      { key: "lifecycle-attendance", to: "/people/lifecycle/attendance", label: "hr.tabs.attendance" },
      { key: "lifecycle-roster", to: "/people/lifecycle/roster", label: "rst.tab" },
      { key: "lifecycle-hiring", to: "/people/lifecycle/hiring", label: "hr.tabs.hiring" },
      { key: "lifecycle-movements", to: "/people/lifecycle/movements", label: "hr.tabs.movements" },
      { key: "lifecycle-exits", to: "/people/lifecycle/exits", label: "hr.tabs.exits" },
      { key: "lifecycle-org", to: "/people/lifecycle/org", label: "hr.tabs.org" },
      { key: "lifecycle-payroll", to: "/people/lifecycle/payroll", label: "hr.tabs.payroll", roles: ["os_hr", ...FINANCE] },
      { key: "recruitment-request", to: "/people/recruitment/request", label: "req.navLabel" },
      { key: "recruitment-channels", to: "/people/recruitment/channels", label: "rec.tabs.channels" },
      { key: "recruitment-paperwork", to: "/people/recruitment/paperwork", label: "rec.tabs.paperwork" },
      { key: "recruitment-correspondence", to: "/people/recruitment/correspondence", label: "rec.tabs.correspondence" },
      { key: "learning-programmes", to: "/people/learning/programmes", label: "ld.tabs.programmes" },
      { key: "learning-needs", to: "/people/learning/needs", label: "ld.tabs.needs" },
      { key: "learning-matrix", to: "/people/learning/matrix", label: "ld.tabs.matrix" },
      { key: "learning-succession", to: "/people/learning/succession", label: "ld.tabs.succession" },
    ] },
  { key: "contractors", to: "/contractors", label: "nav.contractors.title", desc: "nav.contractors.desc", color: "bg-brand-deep",
    roles: ["os_procurement", "os_project_manager", "os_plant_gm", "os_hr", ...FINANCE],
    entities: [
      { key: "board", to: "/contractors", label: "nav.contractors.board", paths: ["/contractors/"] },
      { key: "contractor-new", to: "/contractors/new", label: "con.form.addTitle" },
    ] },
  { key: "product", to: "/warehouse", label: "nav.product.title", desc: "nav.product.desc", color: "bg-brand-primary", roles: STOCK,
    entities: [] },
  { key: "orders", to: "/orders", label: "nav.orders.title", desc: "nav.orders.desc", color: "bg-brand-deep", roles: ["os_hr", ...COMMERCIAL],
    entities: [
      { key: "pipeline", to: "/orders?tab=pipeline", label: "nav.orders.pipeline" },
      { key: "orders", to: "/orders?tab=orders", label: "nav.orders.orders" },
      // finance's work queue (held / to deliver / to invoice / to collect) and the CRM funnel (OS-0670, OS-0672)
      { key: "queue", to: "/orders?tab=queue", label: "nav.orders.queue" },
      { key: "crm", to: "/orders?tab=crm", label: "nav.orders.crm" },
      // the customer form (D-142) with the terms the order rules read (OS-0671); who may create one is api/master.WRITE_ROLES
      { key: "customer-new", to: "/customers/new", label: "master.new.customer", roles: ["os_sales", "os_finance", "os_admin"] },
      { key: "pos", to: "/pos", label: "nav.orders.pos", roles: ["os_cashier", "os_store_manager", ...FINANCE] },
    ] },
  { key: "procurement", to: "/procure", label: "nav.procurement.title", desc: "nav.procurement.desc", color: "bg-brand-primary", roles: BUYERS,
    entities: [
      { key: "pipeline", to: "/procure?tab=pipeline", label: "nav.procurement.pipeline" },
      { key: "requests", to: "/procure?tab=requests", label: "nav.procurement.requests", paths: ["/procure/request/"] },
      { key: "orders", to: "/procure?tab=orders", label: "nav.procurement.orders" },
      { key: "onboarding", to: "/procure?tab=suppliers", label: "nav.procurement.onboarding", paths: ["/procure/onboarding/"], roles: ["os_procurement", ...FINANCE] },
      { key: "newSupplier", to: "/suppliers/new", label: "nav.procurement.newSupplier", paths: ["/suppliers/new"] },
      { key: "vendorRegister", to: "/procure?tab=register", label: "nav.procurement.vendorRegister" },
      { key: "sourcing", to: "/projects/sourcing", label: "nav.procurement.sourcing" },
      { key: "labour", to: "/projects/labour", label: "nav.projects.labour" },
    ] },
  // Inventory lands on the warehouse console rather than on an ERP list: the module's own screens come first and the
  // module whose front door they were not allowed through.
  { key: "inventory", to: "/warehouse", label: "nav.inventory.title", desc: "nav.inventory.desc", color: "bg-brand-deep", roles: STOCK,
    entities: [
      ...WAREHOUSE_SECTIONS.map((s) => ({ key: s.key, to: warehousePath(s), label: s.label, roles: s.roles })),] },
  { key: "plant", to: "/operations", label: "nav.plant.title", desc: "nav.plant.desc", color: "bg-brand-primary", roles: PLANT,
    entities: [
      { key: "dashboard", to: "/operations", label: "nav.plant.dashboard" },
    ] },
  // OneFleet: the /fleet module of the add-on (OneFleet AD-02 — promoted from a Plant operations screen to a module of
  // its own; its sections and their roles come from the module's registry so the two never disagree). The sections
  // come first, then their screens — like the second-level People screens, so the breadcrumb names a screen
  // instead of printing its path segment and the module menu lists the sections first.
  { key: "fleet", to: "/fleet", label: "nav.fleet.title", desc: "nav.fleet.desc", color: "bg-brand-primary", roles: FLEET_MODULE_ROLES,
    entities: [
      ...FLEET_SECTIONS.filter((s) => s.path).map((s) => ({ key: s.key, to: fleetPath(s), label: s.label, roles: s.roles })),
      ...FLEET_SECTIONS.flatMap((s) => s.screens.filter((x) => x.path).map((x) => ({ key: `${s.key}-${x.key}`, to: fleetPath(s, x), label: x.label, roles: x.roles ?? s.roles }))),
    ] },
  { key: "receivables", to: "/orders?tab=collections", label: "nav.receivables.title", desc: "nav.receivables.desc", color: "bg-brand-deep", roles: ["os_sales", ...FINANCE],
    entities: [
      { key: "collections", to: "/orders?tab=collections", label: "nav.receivables.collections" },
    ] },
  // Payables opens the orders to bill and pay; it used to open the preferred-vendor register, which is a qualification list
  { key: "payables", to: "/procure?tab=orders", label: "nav.payables.title", desc: "nav.payables.desc", color: "bg-brand-primary", roles: ["os_procurement", "os_plant_gm", ...FINANCE],
    entities: [
      { key: "register", to: "/procure?tab=orders", label: "nav.payables.register" },
    ] },
  { key: "projects", to: "/projects", label: "nav.projects.title", desc: "nav.projects.desc", color: "bg-brand-deep", roles: BUYERS,
    entities: [
      { key: "portfolio", to: "/projects", label: "nav.projects.portfolio" },
    ] },
  // the CRM tile opens the funnel — leads, opportunities, quotations (OS-0670) — not the order pipeline
  { key: "crm", to: "/orders?tab=crm", label: "nav.crm.title", desc: "nav.crm.desc", color: "bg-brand-deep", roles: ["os_sales", "os_plant_gm", ...FINANCE],
    entities: [
      { key: "crm", to: "/orders?tab=crm", label: "nav.orders.crm" },
      { key: "pipeline", to: "/orders?tab=pipeline", label: "nav.orders.pipeline" },
    ] },
  { key: "incentives", to: "/orders?tab=incentives", label: "nav.incentives.title", desc: "nav.incentives.desc", color: "bg-brand-primary", roles: ["os_sales", "os_hr", "os_plant_gm", ...FINANCE],
    entities: [
      { key: "statements", to: "/orders?tab=incentives", label: "nav.incentives.statements", paths: ["/orders/statement/"] },
    ] },
  { key: "admin", to: "/admin", label: "nav.admin.title", desc: "nav.admin.desc", color: "bg-brand-red", roles: ADMIN, system: true,
    entities: [
      { key: "consoles", to: "/admin?tab=consoles", label: "nav.admin.consoles" },
      { key: "config", to: "/admin?tab=config", label: "nav.admin.config" },
      { key: "roles", to: "/admin?tab=roles", label: "nav.admin.roles" },
      { key: "ai", to: "/admin?tab=ai", label: "nav.admin.ai" },
      { key: "addons", to: "/admin?tab=addons", label: "nav.admin.addons" },
    ] },
];

/** The eleven business lines in order (the module keys that are not cross-cutting). */
export const BUSINESS_MODULES = NAV.filter((m) => !m.system && m.key !== "executive").map((m) => m.key);

export function canSee(roles: readonly string[] | undefined, userRoles: readonly string[]): boolean {
  return !roles || roles.length === 0 || userRoles.some((r) => roles.includes(r));
}

/** Modules (with their visible entities) for a set of realm roles. No module addresses an ERPNext screen any more:
 * OneSphere's users work OneSphere's modules, and an administrator who needs the desk opens ERPNext's own URL
 * (owner's rule, 2026-09-28). */
export function modulesFor(userRoles: readonly string[]): NavModule[] {
  return NAV.filter((m) => canSee(m.roles, userRoles)).map((m) => ({ ...m, entities: m.entities.filter((e) => canSee(e.roles ?? m.roles, userRoles)) }));
}

/** Label of an entity: the i18n key translated by the caller, or the ERP registry label. */
export function entityLabel(e: NavEntity, lng: string, t: (key: string) => string): string {
  if (e.doctype) return screenLabel(SCREENS.find((s) => s.doctype === e.doctype), lng, e.doctype);
  return t(e.label ?? e.key);
}

export type Crumb = { label: string; to?: string; literal?: boolean };

function decode(s: string): string {
  try { return decodeURIComponent(s); } catch { return s; }
}
function pathOf(to: string): string {
  return decode(to.split("?")[0]);
}
function tabOf(to: string): string | null {
  const q = to.split("?")[1];
  return q ? new URLSearchParams(q).get("tab") : null;
}
function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix.endsWith("/") ? prefix : prefix + "/");
}

export type Resolved = { module: NavModule; entity: NavEntity | null; rest: string[] };

/** The module, entity and record segments a location belongs to (see the header for the matching rules). */
export function resolve(pathname: string, search: string = ""): Resolved | null {
  const path = decode(pathname);
  const tab = new URLSearchParams(search).get("tab");
  let best: { module: NavModule; entity: NavEntity | null; prefix: string; score: number } | null = null;
  const consider = (module: NavModule, entity: NavEntity | null, prefix: string, score: number) => {
    if (!best || score > best.score) best = { module, entity, prefix, score };
  };
  for (const m of NAV) {
    if (under(path, pathOf(m.to))) consider(m, null, pathOf(m.to), pathOf(m.to).length);
    for (const e of m.entities) {
      const ep = pathOf(e.to);
      const et = tabOf(e.to);
      for (const p of e.paths ?? []) if (path.startsWith(decode(p))) consider(m, e, decode(p), 50 + p.length);
      if (et) {
        if (path === ep && tab === et) consider(m, e, ep, 100 + ep.length);
      } else if (under(path, ep)) consider(m, e, ep, 10 + ep.length);
    }
  }
  if (!best) return null;
  const b: { module: NavModule; entity: NavEntity | null; prefix: string; score: number } = best;
  return { module: b.module, entity: b.entity, rest: path.slice(b.prefix.length).split("/").filter(Boolean) };
}

/** Breadcrumb trail for a location; labels are i18n keys unless `literal` (ERP labels and record names). */
export function crumbsFor(pathname: string, search: string = "", lng: string = "en"): Crumb[] {
  const home: Crumb = { label: "nav.home", to: "/" };
  const r = resolve(pathname, search);
  if (!r) return pathname === "/" || pathname === "" ? [home] : [home, { label: decode(pathname).split("/").filter(Boolean).join(" / "), literal: true }];
  const crumbs: Crumb[] = [home, { label: r.module.label, to: r.module.to }];
  if (r.module.key === "my" && r.module.to === decode(pathname)) return crumbs;
  const rest = [...r.rest];
  // an entity that is the module's own page (the portfolio of Projects, "all screens" of ERP setup) adds no crumb of its own
  const redundant = !!r.entity && !r.entity.doctype && !tabOf(r.entity.to) && pathOf(r.entity.to) === pathOf(r.module.to);
  if (r.entity && !redundant) {
    if (r.entity.doctype) crumbs.push({ label: screenLabel(SCREENS.find((s) => s.doctype === r.entity!.doctype), lng, r.entity.doctype), to: r.entity.to, literal: true });
    else crumbs.push({ label: r.entity.label ?? r.entity.key, to: r.entity.to });
  }
  if (rest.length > 0) crumbs.push({ label: rest.join(" / "), literal: true });
  return crumbs;
}
